import React, { useState, useEffect } from "react";
import {
  incidentApi,
  INCIDENT_PRIORITIES,
  INCIDENT_STATUSES,
  INCIDENT_CATEGORIES,
  CATEGORY_LABELS,
  type Incident,
  type IncidentStats,
  type Recommendation,
} from "../api/incidentApi";
import {
  remediationApi,
  REMEDIATION_STATUSES,
  STATUS_LABELS,
  TYPE_LABELS,
} from "../api/remediationApi";
import StreamingStatus from "@/components/StreamingStatus";

const IncidentManagement = () => {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [stats, setStats] = useState<IncidentStats>({} as IncidentStats);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [filter, setFilter] = useState<{
    status: string;
    priority: string;
    category: string;
    assigned_to: string;
  }>({
    status: "",
    priority: "",
    category: "",
    assigned_to: "",
  });
  const [showRecommendations, setShowRecommendations] = useState<boolean>(false);
  const [validationFeedback, setValidationFeedback] = useState<Record<string, string>>({});
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [remediationRecommendations, setRemediationRecommendations] = useState<
    Record<string, any[]>
  >({});
  const [loadingRemediation, setLoadingRemediation] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [incidentsData, statsData] = await Promise.all([
        incidentApi.getIncidents({ ...filter, limit: 50 }),
        incidentApi.getIncidentStats(),
      ]);
      setIncidents(incidentsData);
      setStats(statsData);
      setError(null);
    } catch (err) {
      setError("Failed to load incidents");
      console.error("Error loading incidents:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewIncident = async (incident: Incident) => {
    try {
      setSelectedIncident(incident);
      const recs = await incidentApi.getRecommendations(incident.id);
      setRecommendations(recs);
      setShowRecommendations(true);
    } catch (err) {
      setError("Failed to load incident details");
      console.error("Error loading incident details:", err);
    }
  };

  const handleAssignAIAgent = async (incidentId: string) => {
    try {
      await incidentApi.assignAIAgent(incidentId);
      loadData(); // Refresh data
    } catch (err) {
      setError("Failed to assign AI agent");
      console.error("Error assigning AI agent:", err);
    }
  };

  const handleValidateRecommendation = async (recommendationId: string, validated: boolean) => {
    try {
      const feedback = validationFeedback[recommendationId] || "";
      await incidentApi.validateRecommendation(recommendationId, validated, feedback);

      // Refresh recommendations
      if (selectedIncident) {
        const recs = await incidentApi.getRecommendations(selectedIncident.id);
        setRecommendations(recs);
      }

      // Clear feedback
      setValidationFeedback((prev) => ({ ...prev, [recommendationId]: "" }));
    } catch (err) {
      setError("Failed to validate recommendation");
      console.error("Error validating recommendation:", err);
    }
  };

  const handleImplementRecommendation = async (recommendationId: string) => {
    try {
      await incidentApi.implementRecommendation(recommendationId);

      // Refresh recommendations
      if (selectedIncident) {
        const recs = await incidentApi.getRecommendations(selectedIncident.id);
        setRecommendations(recs);
      }
    } catch (err) {
      setError("Failed to implement recommendation");
      console.error("Error implementing recommendation:", err);
    }
  };

  // ============= Remediation Recommendations Functions =============

  const fetchRemediationRecommendations = async (incidentId: string) => {
    try {
      setLoadingRemediation((prev) => ({ ...prev, [incidentId]: true }));
      const recommendations = await remediationApi.getIncidentRecommendations(incidentId);
      setRemediationRecommendations((prev) => ({
        ...prev,
        [incidentId]: recommendations,
      }));
    } catch (error) {
      console.error("Error fetching remediation recommendations:", error);
      // Auto-generate if none exist
      try {
        const generated = await remediationApi.autoGenerateRecommendations(incidentId);
        setRemediationRecommendations((prev) => ({
          ...prev,
          [incidentId]: generated,
        }));
      } catch (genError) {
        console.error("Error auto-generating recommendations:", genError);
      }
    } finally {
      setLoadingRemediation((prev) => ({ ...prev, [incidentId]: false }));
    }
  };

  const handleApproveRemediation = async (recommendationId: string, incidentId: string) => {
    try {
      await remediationApi.approveRecommendation(recommendationId, "current-user");
      await fetchRemediationRecommendations(incidentId);
    } catch (error) {
      console.error("Error approving remediation:", error);
    }
  };

  const handleRejectRemediation = async (
    recommendationId: string,
    incidentId: string,
    reason: string
  ) => {
    try {
      await remediationApi.rejectRecommendation(recommendationId, "current-user", reason);
      await fetchRemediationRecommendations(incidentId);
    } catch (error) {
      console.error("Error rejecting remediation:", error);
    }
  };

  const handleExecuteRemediation = async (recommendationId: string, incidentId: string) => {
    try {
      await remediationApi.executeRecommendation(recommendationId, "current-user");
      await fetchRemediationRecommendations(incidentId);
    } catch (error) {
      console.error("Error executing remediation:", error);
    }
  };

  const handleCompleteRemediation = async (
    recommendationId: string,
    incidentId: string,
    success: boolean,
    executionTime: number,
    feedback: string
  ) => {
    try {
      await remediationApi.completeExecution(recommendationId, success, executionTime, feedback);
      await fetchRemediationRecommendations(incidentId);

      // Update incident resolution notes if successful
      if (success) {
        const incident = incidents.find((inc) => inc.id === incidentId);
        if (incident) {
          const updatedNotes =
            `${incident.resolution_notes || ""}\n\nRemediation completed successfully: ${feedback || "No additional notes"}`.trim();
          await incidentApi.updateIncident(incidentId, {
            resolution_notes: updatedNotes,
            status: "RESOLVED",
          });
        }
      }
    } catch (error) {
      console.error("Error completing remediation:", error);
    }
  };

  const handleCreateMockData = async () => {
    try {
      await incidentApi.createMockIncidents();
      loadData();
    } catch (err) {
      setError("Failed to create mock incidents");
      console.error("Error creating mock incidents:", err);
    }
  };

  const getPriorityBadgeClass = (priority: string) => {
    switch (priority) {
      case "CRITICAL":
        return "bg-red-100 text-red-800";
      case "HIGH":
        return "bg-orange-100 text-orange-800";
      case "MEDIUM":
        return "bg-yellow-100 text-yellow-800";
      case "LOW":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "NEW":
        return "bg-blue-100 text-blue-800";
      case "IN_PROGRESS":
        return "bg-purple-100 text-purple-800";
      case "PENDING_VALIDATION":
        return "bg-yellow-100 text-yellow-800";
      case "RESOLVED":
        return "bg-green-100 text-green-800";
      case "CLOSED":
        return "bg-gray-100 text-gray-800";
      case "CANCELLED":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Incident Management</h1>
          <p className="text-gray-600 mt-1">ServiceNow incidents with AI-powered recommendations</p>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Streaming Status */}
      <StreamingStatus
        title="Incident Agent Activity"
        filters={{
          agent_types: ["ai_agent", "rag_agent", "remediation_agent"],
          log_levels: ["info", "warning", "error", "success"],
        }}
        compact={true}
        showLogs={true}
        maxMessages={20}
      />

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-md">
              <span className="text-2xl">🎫</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Incidents</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.total_incidents || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-md">
              <span className="text-2xl">🆕</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">New (24h)</p>
              <p className="text-2xl font-semibold text-yellow-600">
                {stats.recent_incidents_24h || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-md">
              <span className="text-2xl">⚡</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">In Progress</p>
              <p className="text-2xl font-semibold text-purple-600">
                {stats.status_distribution?.IN_PROGRESS || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-md">
              <span className="text-2xl">🤖</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">AI Assigned</p>
              <p className="text-2xl font-semibold text-green-600">
                {stats.ai_agent_assigned || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-md">
              <span className="text-2xl">📊</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">AI Rate</p>
              <p className="text-2xl font-semibold text-blue-600">
                {Math.round(stats.ai_assignment_rate || 0)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="border border-gray-300 rounded px-3 py-2"
          >
            <option value="">All Statuses</option>
            {Object.entries(INCIDENT_STATUSES).map(([key, value]: [string, string]) => (
              <option key={key} value={value}>
                {key.replace("_", " ")}
              </option>
            ))}
          </select>

          <select
            value={filter.priority}
            onChange={(e) => setFilter({ ...filter, priority: e.target.value })}
            className="border border-gray-300 rounded px-3 py-2"
          >
            <option value="">All Priorities</option>
            {Object.entries(INCIDENT_PRIORITIES).map(([key, value]: [string, string]) => (
              <option key={key} value={value}>
                {key}
              </option>
            ))}
          </select>

          <select
            value={filter.category}
            onChange={(e) => setFilter({ ...filter, category: e.target.value })}
            className="border border-gray-300 rounded px-3 py-2"
          >
            <option value="">All Categories</option>
            {Object.entries(INCIDENT_CATEGORIES).map(([key, value]: [string, string]) => (
              <option key={key} value={value}>
                {CATEGORY_LABELS[value] || key.replace("_", " ")}
              </option>
            ))}
          </select>

          <button
            onClick={() => setFilter({ status: "", priority: "", category: "", assigned_to: "" })}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Incidents Table */}
      <div className="bg-white shadow rounded-lg overflow-x-auto">
        <div className="min-w-full">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                  Incident
                </th>
                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  Priority
                </th>
                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  Status
                </th>
                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Category
                </th>
                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Assigned To
                </th>
                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Created
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {incidents.map((incident: Incident) => (
                <tr key={incident.id} className="hover:bg-gray-50">
                  <td className="px-3 py-3">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {incident.external_ticket_id}
                      </div>
                      <div className="text-xs text-gray-500 truncate max-w-xs">
                        {incident.title}
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-3">
                    <span
                      className={`inline-flex px-1 py-1 text-xs font-semibold rounded ${getPriorityBadgeClass(incident.priority)}`}
                    >
                      {incident.priority === "CRITICAL"
                        ? "CRIT"
                        : incident.priority === "MEDIUM"
                          ? "MED"
                          : incident.priority}
                    </span>
                  </td>
                  <td className="px-2 py-3">
                    <span
                      className={`inline-flex px-1 py-1 text-xs font-semibold rounded ${getStatusBadgeClass(incident.status)}`}
                    >
                      {incident.status === "IN_PROGRESS"
                        ? "PROG"
                        : incident.status === "PENDING_VALIDATION"
                          ? "PEND"
                          : incident.status}
                    </span>
                  </td>
                  <td className="px-2 py-3 text-xs text-gray-900">
                    <div
                      className="truncate max-w-24"
                      title={
                        incident.category
                          ? CATEGORY_LABELS[incident.category] || incident.category
                          : "Unknown"
                      }
                    >
                      {incident.category
                        ? (CATEGORY_LABELS[incident.category] || incident.category).split(" ")[0]
                        : "Unknown"}
                    </div>
                  </td>
                  <td className="px-2 py-3 text-xs text-gray-900">
                    <div className="truncate max-w-24" title={incident.assigned_to || "Unassigned"}>
                      {incident.ai_agent_assigned ? (
                        <span className="text-green-600 font-medium">AI Agent</span>
                      ) : (
                        incident.assigned_to || "Unassigned"
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-3 text-xs text-gray-500">
                    <div className="truncate">
                      {new Date(incident.created_at).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-xs font-medium">
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => handleViewIncident(incident)}
                        className="bg-blue-500 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs"
                      >
                        View
                      </button>
                      {!incident.ai_agent_assigned && (
                        <button
                          onClick={() => handleAssignAIAgent(incident.id)}
                          className="bg-green-500 hover:bg-green-700 text-white px-2 py-1 rounded text-xs"
                        >
                          AI
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {incidents.length === 0 && (
            <div className="text-center py-12">
              <span className="text-4xl">🎫</span>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No incidents</h3>
              <p className="mt-1 text-sm text-gray-500">Create some mock data to get started.</p>
              <button
                onClick={handleCreateMockData}
                className="mt-4 bg-blue-500 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium"
              >
                Create Mock Data
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Incident Details Modal with Recommendations */}
      {showRecommendations && selectedIncident && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {selectedIncident.external_ticket_id}: {selectedIncident.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {selectedIncident.category
                      ? CATEGORY_LABELS[selectedIncident.category]
                      : "Unknown"}{" "}
                    • {selectedIncident.priority} Priority
                  </p>
                </div>
                <button
                  onClick={() => setShowRecommendations(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              {/* Incident Description */}
              <div className="mb-6 p-4 bg-gray-50 rounded-md">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Description</h4>
                <p className="text-sm text-gray-700">{selectedIncident.description}</p>
                {selectedIncident.affected_services.length > 0 && (
                  <div className="mt-2">
                    <span className="text-xs font-medium text-gray-600">Affected Services: </span>
                    <span className="text-xs text-gray-600">
                      {selectedIncident.affected_services.join(", ")}
                    </span>
                  </div>
                )}
              </div>

              {/* AI Recommendations */}
              <div className="mb-4">
                <h4 className="text-md font-medium text-gray-900 mb-3">
                  AI Recommendations ({recommendations.length})
                </h4>

                {recommendations.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-md">
                    <span className="text-2xl">🤖</span>
                    <p className="text-sm text-gray-600 mt-2">
                      {selectedIncident.ai_agent_assigned
                        ? "No recommendations generated yet"
                        : "No AI agent assigned to this incident"}
                    </p>
                    {!selectedIncident.ai_agent_assigned && (
                      <button
                        onClick={() => handleAssignAIAgent(selectedIncident.id)}
                        className="mt-2 bg-green-500 hover:bg-green-700 text-white text-sm py-2 px-4 rounded"
                      >
                        Assign AI Agent
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {recommendations.map((rec: Recommendation, index: number) => {
                      // Parse structured recommendations if they're in JSON format
                      let structuredRecs = [];
                      try {
                        if (typeof rec.content === "string" && rec.content.startsWith("[")) {
                          structuredRecs = JSON.parse(rec.content);
                        }
                      } catch (e) {
                        console.log("Failed to parse structured recommendations, using fallback");
                      }

                      if (structuredRecs.length > 0) {
                        // Display enhanced structured recommendations
                        return structuredRecs.map((structRec: any, structIndex: number) => (
                          <div
                            key={`${rec.id}-${structIndex}`}
                            className="border border-gray-200 rounded-lg p-4 bg-gradient-to-r from-blue-50 to-indigo-50"
                          >
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h5 className="font-semibold text-gray-900">{structRec.title}</h5>
                                  <span
                                    className={`text-xs px-2 py-1 rounded-full font-medium ${
                                      structRec.type === "immediate_action"
                                        ? "bg-red-100 text-red-700"
                                        : structRec.type === "investigation_step"
                                          ? "bg-yellow-100 text-yellow-700"
                                          : "bg-green-100 text-green-700"
                                    }`}
                                  >
                                    {structRec.type.replace("_", " ").toUpperCase()}
                                  </span>
                                </div>

                                <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                                  {structRec.description}
                                </p>

                                {structRec.steps && structRec.steps.length > 0 && (
                                  <div className="mb-3 bg-white rounded-md p-3 border border-gray-200">
                                    <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                                      📋 Action Steps:
                                    </p>
                                    <ol className="text-sm text-gray-700 space-y-1">
                                      {structRec.steps.map((step: any, idx: number) => (
                                        <li key={idx} className="flex items-start gap-2">
                                          <span className="flex-shrink-0 w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center font-semibold">
                                            {idx + 1}
                                          </span>
                                          <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                                            {step}
                                          </code>
                                        </li>
                                      ))}
                                    </ol>
                                  </div>
                                )}

                                <div className="flex items-center gap-6 text-sm">
                                  <div className="flex items-center gap-2">
                                    <span className="text-gray-600">🎯 Confidence:</span>
                                    <div className="flex items-center gap-1">
                                      <div className="w-16 h-2 bg-gray-200 rounded-full">
                                        <div
                                          className={`h-2 rounded-full ${
                                            structRec.confidence >= 0.8
                                              ? "bg-green-500"
                                              : structRec.confidence >= 0.6
                                                ? "bg-yellow-500"
                                                : "bg-red-500"
                                          }`}
                                          style={{ width: `${structRec.confidence * 100}%` }}
                                        />
                                      </div>
                                      <span className="text-xs font-semibold">
                                        {Math.round(structRec.confidence * 100)}%
                                      </span>
                                    </div>
                                  </div>
                                  {structRec.estimated_time && (
                                    <div className="flex items-center gap-1">
                                      <span className="text-gray-600">⏱️ Est. Time:</span>
                                      <span className="font-semibold text-blue-700">
                                        {structRec.estimated_time}min
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="ml-4 flex items-center gap-2">
                                {rec.human_validated === null ? (
                                  <div className="flex flex-col gap-2">
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => handleValidateRecommendation(rec.id, true)}
                                        className="bg-green-500 hover:bg-green-700 text-white text-xs py-2 px-3 rounded-md font-semibold"
                                      >
                                        ✓ Validate
                                      </button>
                                      <button
                                        onClick={() => handleValidateRecommendation(rec.id, false)}
                                        className="bg-red-500 hover:bg-red-700 text-white text-xs py-2 px-3 rounded-md font-semibold"
                                      >
                                        ✗ Reject
                                      </button>
                                    </div>
                                    <textarea
                                      placeholder="Optional feedback..."
                                      value={validationFeedback[rec.id] || ""}
                                      onChange={(e) =>
                                        setValidationFeedback((prev) => ({
                                          ...prev,
                                          [rec.id]: e.target.value,
                                        }))
                                      }
                                      className="text-xs border border-gray-300 rounded px-2 py-1 w-32 h-16"
                                    />
                                  </div>
                                ) : (
                                  <div className="text-center">
                                    <div
                                      className={`text-xs px-3 py-1 rounded-md font-semibold ${
                                        rec.human_validated
                                          ? "bg-green-100 text-green-800"
                                          : "bg-red-100 text-red-800"
                                      }`}
                                    >
                                      {rec.human_validated ? "✓ Validated" : "✗ Rejected"}
                                    </div>
                                    {rec.human_validated && !rec.implemented && (
                                      <button
                                        onClick={() => handleImplementRecommendation(rec.id)}
                                        className="mt-2 bg-blue-500 hover:bg-blue-700 text-white text-xs py-1 px-3 rounded-md font-semibold"
                                      >
                                        🚀 Implement
                                      </button>
                                    )}
                                    {rec.implemented && (
                                      <div className="mt-2 text-xs bg-blue-100 text-blue-800 px-3 py-1 rounded-md font-semibold">
                                        ✅ Implemented
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ));
                      } else {
                        // Fallback to original format
                        return (
                          <div key={rec.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <h5 className="font-medium text-gray-900">{rec.title}</h5>
                                <p className="text-sm text-gray-600 mt-1">{rec.description}</p>

                                {rec.steps && rec.steps.length > 0 && (
                                  <div className="mt-2">
                                    <p className="text-xs font-medium text-gray-600">Steps:</p>
                                    <ol className="text-xs text-gray-600 ml-4 list-decimal">
                                      {rec.steps.map((step: any, idx: number) => (
                                        <li key={idx}>{step}</li>
                                      ))}
                                    </ol>
                                  </div>
                                )}

                                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                  <span>Confidence: {Math.round(rec.confidence_score * 100)}%</span>
                                  <span>Type: {rec.recommendation_type.replace("_", " ")}</span>
                                  {rec.estimated_time_minutes && (
                                    <span>Est. Time: {rec.estimated_time_minutes}min</span>
                                  )}
                                </div>
                              </div>

                              <div className="ml-4 flex items-center gap-2">
                                {rec.human_validated === null ? (
                                  <div className="flex flex-col gap-2">
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => handleValidateRecommendation(rec.id, true)}
                                        className="bg-green-500 hover:bg-green-700 text-white text-xs py-1 px-3 rounded"
                                      >
                                        ✓ Validate
                                      </button>
                                      <button
                                        onClick={() => handleValidateRecommendation(rec.id, false)}
                                        className="bg-red-500 hover:bg-red-700 text-white text-xs py-1 px-3 rounded"
                                      >
                                        ✗ Reject
                                      </button>
                                    </div>
                                    <textarea
                                      placeholder="Optional feedback..."
                                      value={validationFeedback[rec.id] || ""}
                                      onChange={(e) =>
                                        setValidationFeedback((prev) => ({
                                          ...prev,
                                          [rec.id]: e.target.value,
                                        }))
                                      }
                                      className="text-xs border border-gray-300 rounded px-2 py-1 w-32 h-16"
                                    />
                                  </div>
                                ) : (
                                  <div className="text-center">
                                    <div
                                      className={`text-xs px-2 py-1 rounded ${
                                        rec.human_validated
                                          ? "bg-green-100 text-green-800"
                                          : "bg-red-100 text-red-800"
                                      }`}
                                    >
                                      {rec.human_validated ? "Validated" : "Rejected"}
                                    </div>
                                    {rec.human_validated && !rec.implemented && (
                                      <button
                                        onClick={() => handleImplementRecommendation(rec.id)}
                                        className="mt-2 bg-blue-500 hover:bg-blue-700 text-white text-xs py-1 px-3 rounded"
                                      >
                                        Mark Implemented
                                      </button>
                                    )}
                                    {rec.implemented && (
                                      <div className="mt-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                        Implemented
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      }
                    })}
                  </div>
                )}
              </div>

              {/* Automated Remediation Recommendations */}
              <RemediationRecommendationsSection
                incident={selectedIncident}
                recommendations={remediationRecommendations[selectedIncident.id] || []}
                loading={loadingRemediation[selectedIncident.id] || false}
                onFetch={() => fetchRemediationRecommendations(selectedIncident.id)}
                onApprove={(recId) => handleApproveRemediation(recId, selectedIncident.id)}
                onReject={(recId, reason) =>
                  handleRejectRemediation(recId, selectedIncident.id, reason)
                }
                onExecute={(recId) => handleExecuteRemediation(recId, selectedIncident.id)}
                onComplete={(recId, incidentId, success, time, feedback) =>
                  handleCompleteRemediation(recId, incidentId, success, time || 0, feedback || "")
                }
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Remediation Recommendations Section Component
interface RemediationRecommendationsSectionProps {
  incident: Incident | null;
  recommendations: any[];
  loading: boolean;
  onFetch: () => void;
  onApprove: (recommendationId: string, incidentId: string) => void;
  onReject: (recommendationId: string, incidentId: string, reason: string) => void;
  onExecute: (recommendationId: string, incidentId: string) => void;
  onComplete: (
    recommendationId: string,
    incidentId: string,
    success: boolean,
    executionTime: number,
    feedback: string
  ) => void;
}

const RemediationRecommendationsSection = ({
  incident,
  recommendations,
  loading,
  onFetch,
  onApprove,
  onReject,
  onExecute,
  onComplete,
}: RemediationRecommendationsSectionProps) => {
  const [executionFeedback, setExecutionFeedback] = useState<Record<string, string>>({});
  const [executionTime, setExecutionTime] = useState<Record<string, number>>({});

  useEffect(() => {
    if (incident && recommendations.length === 0 && !loading) {
      onFetch();
    }
  }, [incident?.id]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "GENERATED":
      case "PENDING_APPROVAL":
        return "bg-yellow-100 text-yellow-800";
      case "APPROVED":
        return "bg-green-100 text-green-800";
      case "REJECTED":
        return "bg-red-100 text-red-800";
      case "IN_PROGRESS":
        return "bg-blue-100 text-blue-800";
      case "COMPLETED":
        return "bg-green-100 text-green-800";
      case "FAILED":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "IMMEDIATE_FIX":
        return "bg-red-100 text-red-700";
      case "PREVENTIVE_MEASURE":
        return "bg-blue-100 text-blue-700";
      case "CONFIGURATION_CHANGE":
        return "bg-purple-100 text-purple-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-3">
        <h4 className="text-md font-medium text-gray-900">
          🛠️ Automated Remediation ({recommendations.length})
        </h4>
        <button
          onClick={onFetch}
          disabled={loading}
          className="bg-blue-500 hover:bg-blue-700 text-white text-xs py-1 px-3 rounded disabled:opacity-50"
        >
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8 bg-gray-50 rounded-md">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-sm text-gray-600 mt-2">Generating remediation recommendations...</p>
        </div>
      ) : recommendations.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-md">
          <span className="text-2xl">🔧</span>
          <p className="text-sm text-gray-600 mt-2">
            No automated remediation recommendations available
          </p>
          <button
            onClick={onFetch}
            className="mt-2 bg-blue-500 hover:bg-blue-700 text-white text-sm py-2 px-4 rounded"
          >
            Generate Recommendations
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {recommendations.map((rec: any) => (
            <div
              key={rec.id}
              className="border border-gray-200 rounded-lg p-4 bg-gradient-to-r from-green-50 to-blue-50"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h5 className="font-semibold text-gray-900">{rec.title}</h5>
                    <span
                      className={`text-xs px-2 py-1 rounded-full font-medium ${getTypeColor(rec.remediation_type)}`}
                    >
                      {TYPE_LABELS[rec.remediation_type] || rec.remediation_type}
                    </span>
                    <span
                      className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(rec.status)}`}
                    >
                      {STATUS_LABELS[rec.status] || rec.status}
                    </span>
                  </div>

                  <p className="text-sm text-gray-700 mb-3 leading-relaxed">{rec.description}</p>

                  {/* Risk Assessment */}
                  {rec.risk_assessment && (
                    <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                      <p className="text-xs font-medium text-yellow-800">⚠️ Risk Assessment:</p>
                      <p className="text-xs text-yellow-700">{rec.risk_assessment}</p>
                    </div>
                  )}

                  {/* Remediation Steps */}
                  {rec.steps && rec.steps.length > 0 && (
                    <div className="mb-3 bg-white rounded-md p-3 border border-gray-200">
                      <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">
                        📋 Remediation Steps:
                      </p>
                      <ol className="text-sm text-gray-700 space-y-2">
                        {rec.steps.map((step: any, idx: number) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="flex-shrink-0 w-5 h-5 bg-green-500 text-white text-xs rounded-full flex items-center justify-center font-semibold">
                              {step.step_number}
                            </span>
                            <div className="flex-1">
                              <div className="font-medium text-sm">{step.title}</div>
                              <div className="text-xs text-gray-600 mt-1">{step.description}</div>
                              {step.command && (
                                <code className="block bg-gray-100 px-2 py-1 rounded text-xs font-mono mt-1">
                                  {step.command}
                                </code>
                              )}
                              <div className="flex gap-4 text-xs text-gray-500 mt-1">
                                <span>⏱️ Est. Time: {step.estimated_duration_minutes}min</span>
                                {step.requires_approval && <span>🔒 Requires Approval</span>}
                                {step.automation_supported && <span>🤖 Automation Ready</span>}
                              </div>
                            </div>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}

                  <div className="flex items-center gap-6 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">🎯 Success Rate:</span>
                      <div className="flex items-center gap-1">
                        <div className="w-16 h-2 bg-gray-200 rounded-full">
                          <div
                            className={`h-2 rounded-full ${
                              rec.success_probability >= 0.8
                                ? "bg-green-500"
                                : rec.success_probability >= 0.6
                                  ? "bg-yellow-500"
                                  : "bg-red-500"
                            }`}
                            style={{ width: `${rec.success_probability * 100}%` }}
                          />
                        </div>
                        <span className="text-xs font-semibold">
                          {Math.round(rec.success_probability * 100)}%
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-600">🚀 Priority:</span>
                      <span className="font-semibold text-blue-700">{rec.priority}</span>
                    </div>
                  </div>
                </div>

                <div className="ml-4 flex flex-col gap-2">
                  {rec.status === "GENERATED" || rec.status === "PENDING_APPROVAL" ? (
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-2">
                        <button
                          onClick={() => incident && onApprove(rec.id, incident.id)}
                          className="bg-green-500 hover:bg-green-700 text-white text-xs py-2 px-3 rounded-md font-semibold"
                        >
                          ✓ Approve
                        </button>
                        <button
                          onClick={() =>
                            incident && onReject(rec.id, incident.id, "Manual rejection")
                          }
                          className="bg-red-500 hover:bg-red-700 text-white text-xs py-2 px-3 rounded-md font-semibold"
                        >
                          ✗ Reject
                        </button>
                      </div>
                    </div>
                  ) : rec.status === "APPROVED" ? (
                    <button
                      onClick={() => incident && onExecute(rec.id, incident.id)}
                      className="bg-blue-500 hover:bg-blue-700 text-white text-xs py-2 px-4 rounded-md font-semibold"
                    >
                      🚀 Execute
                    </button>
                  ) : rec.status === "IN_PROGRESS" ? (
                    <div className="flex flex-col gap-2">
                      <div className="text-xs text-blue-700 font-semibold">⚡ In Progress</div>
                      <input
                        type="number"
                        placeholder="Exec time (min)"
                        value={executionTime[rec.id] || ""}
                        onChange={(e) =>
                          setExecutionTime((prev) => ({
                            ...prev,
                            [rec.id]: e.target.value,
                          }))
                        }
                        className="text-xs border border-gray-300 rounded px-2 py-1 w-20"
                      />
                      <textarea
                        placeholder="Execution notes..."
                        value={executionFeedback[rec.id] || ""}
                        onChange={(e) =>
                          setExecutionFeedback((prev) => ({
                            ...prev,
                            [rec.id]: e.target.value,
                          }))
                        }
                        className="text-xs border border-gray-300 rounded px-2 py-1 w-32 h-16"
                      />
                      <div className="flex gap-1">
                        <button
                          onClick={() =>
                            incident &&
                            onComplete(
                              rec.id,
                              incident.id,
                              true,
                              parseInt(String(executionTime[rec.id] || "")) || 30,
                              executionFeedback[rec.id] || ""
                            )
                          }
                          className="bg-green-500 hover:bg-green-700 text-white text-xs py-1 px-2 rounded font-semibold"
                        >
                          ✅ Success
                        </button>
                        <button
                          onClick={() =>
                            incident &&
                            onComplete(
                              rec.id,
                              incident.id,
                              false,
                              parseInt(String(executionTime[rec.id] || "")) || 30,
                              executionFeedback[rec.id] || ""
                            )
                          }
                          className="bg-red-500 hover:bg-red-700 text-white text-xs py-1 px-2 rounded font-semibold"
                        >
                          ❌ Failed
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div
                        className={`text-xs px-3 py-1 rounded-md font-semibold ${getStatusColor(rec.status)}`}
                      >
                        {rec.status === "COMPLETED"
                          ? "✅ Completed"
                          : rec.status === "FAILED"
                            ? "❌ Failed"
                            : rec.status === "REJECTED"
                              ? "✗ Rejected"
                              : rec.status}
                      </div>
                      {rec.actual_success !== undefined && (
                        <div className="text-xs text-gray-600 mt-1">
                          Result: {rec.actual_success ? "Success" : "Failed"}
                          {rec.execution_time_minutes && ` (${rec.execution_time_minutes}min)`}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default IncidentManagement;
